import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {testIdProps} from '@github-ui/test-id-props'
import type {PreparedBlock} from '@github-ui/taillights-worker/document'
import {clsx} from 'clsx'
import {startTransition, useEffect, useReducer, useRef, type RefObject} from 'react'
import {bindTaillightText} from './taillight-dom'
import {TaillightFrame} from './taillight-frame'
import type {TaillightProps} from './taillight-types'
import {TaillightsGlyphs} from './taillights-glyphs'
import {useTaillightsController, useTaillightsCSSScope} from './taillights-provider'
import styles from './taillight.module.css'

/** Binds prepared syntax ranges to native text and reports binding failures without replacing that text. */
function CSSPaint({block, paintRef}: {block: PreparedBlock; paintRef: RefObject<HTMLPreElement | null>}) {
  const [error, fail] = useReducer((_previous: Error | null, next: Error) => next, null)
  const scope = useTaillightsCSSScope()
  useEffect(() => {
    function reportFailure(failure: unknown) {
      const nextError = failure instanceof Error ? failure : new Error(String(failure))
      startTransition(() => fail(nextError))
    }
    try {
      return scope.register(bindTaillightText(paintRef.current, block.text), block, block.palette.colors)
    } catch (failure) {
      reportFailure(failure)
    }
  }, [block, paintRef, scope])
  if (error) throw error
  return null
}

/** Highlights native text with CSS ranges, preserving its DOM and controls without requesting bitmap paint. */
export function TaillightCSS(props: TaillightProps) {
  const {block, inert, hidePaint, isSelectable = false, 'aria-label': ariaLabel} = props
  const accessibleName = ariaLabel?.trim() || undefined
  const controller = useTaillightsController()
  const paintRef = useRef<HTMLPreElement>(null)
  return (
    <>
      <TaillightFrame {...props} renderer="css" nativeSourceRef={paintRef} controls={props.children}>
        <pre
          {...testIdProps('taillights-chunk')}
          ref={paintRef}
          translate="no"
          className={clsx(styles.Paint, !isSelectable && styles.nonSelectable, hidePaint && styles.hiddenPaint)}
          inert={inert || undefined}
          aria-hidden={inert || hidePaint || undefined}
          role={!inert && accessibleName ? 'region' : undefined}
          aria-label={!inert ? accessibleName : undefined}
          tabIndex={!inert && !hidePaint && isSelectable && accessibleName ? 0 : undefined}
        >
          <TaillightsGlyphs text={block.text} runs={block.glyphRuns} pseudoText={false} />
          {block.text.endsWith('\n') && <br inert aria-hidden />}
        </pre>
      </TaillightFrame>
      {controller && (
        <ErrorBoundary
          key={props.block.imageKey}
          boundaryName="TaillightCSS"
          fallback={null}
          onError={props.onRenderError}
        >
          <CSSPaint block={props.block} paintRef={paintRef} />
        </ErrorBoundary>
      )}
    </>
  )
}
